import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthLayout } from '../../components/auth/AuthLayout';
import { YandexAuthButton } from '../../components/auth/YandexAuthButton';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { InputField } from '../../components/ui/InputField';
import { api, ApiError } from '../../lib/api';
import { getDeviceToken, setTokens } from '../../lib/tokens';

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  denied: 'Вход через Яндекс отменён.',
  state: 'Сессия входа через Яндекс истекла — попробуйте ещё раз.',
  token: 'Яндекс не подтвердил вход — попробуйте ещё раз.',
  userinfo: 'Не удалось получить данные профиля Яндекс — попробуйте ещё раз.',
  no_email: 'В вашем аккаунте Яндекс не указана подтверждённая почта — добавьте её в настройках Яндекс ID и повторите вход.',
  blocked: 'Этот аккаунт заблокирован. Обратитесь к администратору организации.',
};

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oauthError = searchParams.get('oauth_error');
  const [method, setMethod] = useState<'email' | 'phone'>('email');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [identity, setIdentity] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const response = await api.login({
        email: method === 'email' ? identity : undefined,
        phone: method === 'phone' ? identity : undefined,
        password,
        device_token: getDeviceToken() ?? undefined,
      });

      if (response.mfa_required && response.challenge_id) {
        navigate('/mfa', {
          state: { challengeId: response.challenge_id, availableFactors: response.available_factors },
        });
        return;
      }

      if (response.tokens) {
        setTokens(response.tokens.access_token, response.tokens.refresh_token);
        navigate('/workspace');
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось войти. Попробуйте ещё раз.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      topRight={
        <span className="text-text-secondary">
          Нет аккаунта?{' '}
          <Link to="/register" className="font-medium text-action-primary">
            Создать
          </Link>
        </span>
      }
    >
      <form onSubmit={handleSubmit} className="mx-auto flex w-[420px] max-w-full flex-col gap-6">
        <div>
          <h1 className="text-[28px] font-semibold text-text-primary">Вход в КСЕНУС ГИС</h1>
          <p className="mt-2 text-[13px] text-text-secondary">
            Используйте рабочую почту или номер телефона.
          </p>
        </div>

        <div className="flex h-[42px] rounded-[10px] bg-bg-surface-secondary p-1">
          <button
            type="button"
            onClick={() => {
              setMethod('email');
              setIdentity('');
            }}
            className={`flex-1 rounded-lg text-xs font-semibold ${
              method === 'email' ? 'bg-bg-surface text-action-primary shadow-sm' : 'text-text-secondary'
            }`}
          >
            E-mail
          </button>
          <button
            type="button"
            onClick={() => {
              setMethod('phone');
              setIdentity('');
            }}
            className={`flex-1 rounded-lg text-xs font-medium ${
              method === 'phone' ? 'bg-bg-surface text-action-primary shadow-sm' : 'text-text-secondary'
            }`}
          >
            Телефон
          </button>
        </div>

        {method === 'email' ? (
          <InputField
            label="Рабочая почта"
            type="email"
            placeholder="name@company.ru"
            value={identity}
            onChange={(e) => setIdentity(e.target.value)}
            required
          />
        ) : (
          <InputField
            label="Номер телефона"
            type="tel"
            placeholder="+7 999 000-00-00"
            value={identity}
            onChange={(e) => setIdentity(e.target.value)}
            required
          />
        )}
        <InputField
          label="Пароль"
          type="password"
          placeholder="••••••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />

        {error && <p className="text-xs font-medium text-status-warning">{error}</p>}
        {!error && oauthError && (
          <p className="text-xs font-medium text-status-warning">
            {OAUTH_ERROR_MESSAGES[oauthError] ?? 'Не удалось войти через Яндекс. Попробуйте ещё раз.'}
          </p>
        )}

        <div className="flex items-center justify-between">
          <Checkbox
            label="Запомнить это устройство"
            checked={rememberDevice}
            onChange={(e) => setRememberDevice(e.target.checked)}
          />
          <Link to="/reset-password" className="text-xs font-medium text-action-primary">
            Забыли пароль?
          </Link>
        </div>

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? 'Входим…' : 'Войти'}
        </Button>

        <p className="text-center text-[11px] text-text-secondary">── или ──</p>

        <YandexAuthButton />

        <Button type="button" variant="secondary" className="w-full" disabled>
          Войти через SSO / ЕСИА
        </Button>

        <div className="rounded-xl bg-bg-surface-secondary p-4">
          <p className="flex items-center gap-2 text-xs font-semibold text-text-primary">
            <span className="text-status-success">✓</span> Безопасность корпоративного уровня
          </p>
          <p className="mt-1 text-[11px] leading-relaxed text-text-secondary">
            Сессии, MFA и права доступа управляются политиками вашей организации.
          </p>
        </div>
      </form>
    </AuthLayout>
  );
}
